import { useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Package, Tag, ScrollText, LogOut, ArrowLeft, Megaphone, Feather, Users, Truck, BarChart3 } from "lucide-react";

const AdminLayout = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/auth", { state: { from: "/admin" } });
    } else if (!user.is_admin) {
      navigate("/account");
    }
  }, [user, loading, navigate]);

  if (loading || !user || !user.is_admin) {
    return (
      <div className="px-6 py-32 text-muted-bone text-center">Loading…</div>
    );
  }

  const items = [
    { to: "/admin", label: "Dashboard", end: true, icon: LayoutDashboard },
    { to: "/admin/products", label: "Products", icon: Package },
    { to: "/admin/discounts", label: "Discount Codes", icon: Tag },
    { to: "/admin/orders", label: "Orders", icon: ScrollText },
    { to: "/admin/reports", label: "Reports", icon: BarChart3 },
    { to: "/admin/suppliers", label: "Vendors", icon: Truck },
    { to: "/admin/customers", label: "Customers", icon: Users },
    { to: "/admin/marketing", label: "Marketing", icon: Megaphone },
    { to: "/admin/journal", label: "Journal", icon: Feather },
  ];

  return (
    <div data-testid="admin-layout" className="min-h-screen flex">
      <aside
        className="hidden md:flex flex-col w-64 border-r"
        style={{
          background: "var(--rs-surface)",
          borderColor: "var(--rs-border)",
        }}
      >
        <div className="px-6 py-8 border-b" style={{ borderColor: "var(--rs-border)" }}>
          <Link to="/" className="font-serif-display text-2xl text-bone">
            Ram Shack
          </Link>
          <div className="overline mt-2">Atelier — Admin</div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-[0.2em] transition ${
                  isActive ? "text-bone bg-white/5" : "text-muted-bone hover:text-bone"
                }`
              }
            >
              <it.icon strokeWidth={1.5} size={16} />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t space-y-2" style={{ borderColor: "var(--rs-border)" }}>
          <Link
            to="/"
            data-testid="admin-back-to-shop"
            className="flex items-center gap-3 px-4 py-3 text-xs uppercase tracking-[0.22em] text-muted-bone hover:text-bone"
          >
            <ArrowLeft strokeWidth={1.5} size={14} /> Back to shop
          </Link>
          <button
            data-testid="admin-logout-btn"
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-xs uppercase tracking-[0.22em] text-muted-bone hover:text-bone"
          >
            <LogOut strokeWidth={1.5} size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="md:hidden border-b px-6 py-5 flex items-center justify-between"
          style={{ borderColor: "var(--rs-border)", background: "var(--rs-surface)" }}>
          <Link to="/" className="font-serif-display text-xl text-bone">Ram Shack · Admin</Link>
          <div className="flex gap-3">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) => `text-xs uppercase tracking-widest ${isActive ? "text-bone" : "text-muted-bone"}`}
              >
                {it.label.split(" ")[0]}
              </NavLink>
            ))}
          </div>
        </div>
        <div className="p-6 md:p-10 lg:p-14 max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
